/**
 * Temporary Coins Service for prototyping purposes
 * Manages a local storage variable for temporary coin balance
 * This will be removed in future versions when backend integration is complete
 */
export class TemporaryCoinsService {
  private static readonly STORAGE_KEY = "temporary_coins";
  private static readonly DEFAULT_COINS = 1000;

  /**
   * Get current temporary coin balance
   * Returns default value if not set
   */
  static getCoins(): number {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored === null) {
        // Initialize with default value if not set
        this.setCoins(this.DEFAULT_COINS);
        return this.DEFAULT_COINS;
      }
      return parseInt(stored, 10) || this.DEFAULT_COINS;
    } catch (error) {
      console.error("Failed to get temporary coins:", error);
      return this.DEFAULT_COINS;
    }
  }

  /**
   * Set temporary coin balance
   */
  static setCoins(amount: number): void {
    try {
      const coins = Math.max(0, Math.floor(amount)); // Ensure non-negative integer
      localStorage.setItem(this.STORAGE_KEY, coins.toString());
    } catch (error) {
      console.error("Failed to set temporary coins:", error);
    }
  }

  /**
   * Add coins to current balance
   */
  static addCoins(amount: number): number {
    const currentCoins = this.getCoins();
    const newBalance = currentCoins + Math.floor(amount);
    this.setCoins(newBalance);
    return newBalance;
  }

  /**
   * Subtract coins from current balance
   * Returns true if successful, false if insufficient funds
   */
  static subtractCoins(amount: number): boolean {
    const currentCoins = this.getCoins();
    const subtractAmount = Math.floor(amount);

    if (currentCoins >= subtractAmount) {
      this.setCoins(currentCoins - subtractAmount);
      return true;
    }
    return false; // Insufficient funds
  }

  /**
   * Check if user has enough coins
   */
  static hasEnoughCoins(amount: number): boolean {
    return this.getCoins() >= Math.floor(amount);
  }

  /**
   * Reset to default coin amount
   */
  static reset(): void {
    this.setCoins(this.DEFAULT_COINS);
  }

  /**
   * Clear temporary coins from storage
   */
  static clear(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      console.error("Failed to clear temporary coins:", error);
    }
  }
}
